// Movement HUD: acceleration bar, angle readout and the sampled cgaz strip.
//
// The cgaz strip (docs/cgaz-research.md) is the only movement helper now; the
// old strafe/bunny triangles were retired once the real sampled overlay
// shipped. The angle readout still reports the branch's optimal VIEW angle
// (`aim`): `opt-45°` for W+A/D, `90-opt` for A/D-only and 0 for W-only.
//
// The acceleration bar is driven by the sim's real per-tick speed change
// (hint[17]), not by `dot(velocity, wishdir)`.

import { TICK_DT } from "../tickrate";
import { hudStageSize } from "../stage";
import { DEFAULT_CGAZ_STYLE, type CgazStyle, type CgazZone } from "../settings";

// Branch ids from the sim (`MOVE_MODE_*`): 2 W+A/D, 3 A/D only, 4 W only.
const MODE_STRAFE_JUMP = 2;
const MODE_STRAFE_BUNNY = 3;

/** cgaz probe range; keep in sync with `core/src/pmove.rs`. */
const CGAZ_RANGE_DEG = 120;

/** Optimal velocity→wishdir angle for the branch that was actually used. */
function optAngleDeg(wishspeed: number, accel: number, speed: number): number {
  const base = wishspeed * (1 - accel * TICK_DT);
  if (!(base > 0) || !(speed > base)) return 0;
  return (Math.acos(Math.min(1, base / speed)) * 180) / Math.PI;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** #rrggbb + alpha -> canvas rgba(). */
function rgbaHex(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return `rgba(255,255,255,${alpha})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 0xff},${(n >> 8) & 0xff},${n & 0xff},${alpha})`;
}

export class MovementHud {
  private accelFill: HTMLElement;
  private cgazCanvas: HTMLCanvasElement;
  private cgazCtx: CanvasRenderingContext2D | null;
  private angleEl: HTMLElement;
  private lastAngle = "";
  private lastFill = -1;
  private lastAccelColor = "";
  private lastCgazHeight = -1;
  private lastCgazFilter = "";
  private lastCgazOpacity = "";
  private cgazHidden = false;

  constructor() {
    this.accelFill = document.getElementById("accel-fill")!;
    this.cgazCanvas = document.getElementById("cgaz-hud") as HTMLCanvasElement;
    this.cgazCtx = this.cgazCanvas.getContext("2d");
    this.angleEl = document.getElementById("angle-hud")!;
  }

  /**
   * Update from the sim hint (19 floats):
   *   vx, vy, 0 (velocity), wx, wy, 0 (wishdir), speed, dot(vel,wishdir),
   *   fwd, strafe, diff_angle, yaw, pitch (degrees), on_ground, move_mode,
   *   wishspeed, branch_accel, accel_frac, speed_gain
   */
  update(
    hint: Float32Array | number[],
    fovDeg = 90,
    cgazProfile: Float32Array | number[] | null = null,
    cgazStyle: CgazStyle = DEFAULT_CGAZ_STYLE,
    panini = 0,
  ) {
    const speed = hint[6];
    const diffDeg = hint[10] ?? 0;
    const pitchDeg = hint[12] ?? 0;
    const mode = Math.round(hint[14] ?? 0);
    const wishspeed = hint[15] ?? 0;
    const branchAccel = hint[16] ?? 0;
    const accelFrac = clamp(hint[17] ?? 0, -1, 1);

    // --- Acceleration bar: the sim's real Δspeed per tick, normalized by the
    // maximum gain of the branch. Full bar = max acceleration, red = losing.
    const fill = 0.5 + 0.5 * accelFrac;
    const fillPct = Math.round(fill * 1000) / 10;
    if (fillPct !== this.lastFill) {
      this.lastFill = fillPct;
      this.accelFill.style.width = `${fillPct.toFixed(1)}%`;
    }
    const accelColor =
      accelFrac > 0.12
        ? "var(--ui-success)"
        : accelFrac < -0.12
          ? "var(--ui-danger)"
          : "var(--ui-warn)";
    if (accelColor !== this.lastAccelColor) {
      this.lastAccelColor = accelColor;
      this.accelFill.style.background = accelColor;
    }

    const stage = hudStageSize();
    const w = Math.max(200, stage.w);
    const halfFov = Math.max(20, fovDeg * 0.5);
    // Same forward map as the panini composite shader: `d = 0` is exactly
    // rectilinear (tan), `d = 1` is classic Panini. The cgaz must use the
    // projection the world is actually drawn with, or the zones drift outward
    // as the strength rises.
    const d = Math.max(0, panini);
    const hAt = (rad: number): number =>
      ((d + 1) * Math.sin(rad)) / (d + Math.cos(rad));
    const hMax = hAt((halfFov * Math.PI) / 180);
    const project = (deg: number): number => {
      const a = clamp(deg, -halfFov + 0.01, halfFov - 0.01);
      return (w * 0.5) * (hAt((a * Math.PI) / 180) / hMax);
    };

    // Branch optimum → optimal VIEW angle for the readout.
    const opt = optAngleDeg(wishspeed, branchAccel, speed);
    const viewAim =
      mode === MODE_STRAFE_JUMP
        ? Math.max(0, opt - 45)
        : mode === MODE_STRAFE_BUNNY
          ? clamp(90 - opt, 0, 45)
          : 0;

    // --- cgaz acceleration strip (real air_move, or ground_move on slick) ---
    this.drawCgaz(cgazProfile, w, project, speed > 2, cgazStyle);

    // --- Angles (always visible) ---
    const angle =
      `pitch <span class="opt">${pitchDeg.toFixed(0)}°</span> · ` +
      `Δ <span class="diff">${diffDeg.toFixed(0)}°</span> · ` +
      `aim <span class="opt">${viewAim.toFixed(0)}°</span>`;
    if (angle !== this.lastAngle) {
      this.lastAngle = angle;
      this.angleEl.innerHTML = angle;
    }
  }

  /**
   * Draw the sampled cgaz zones. Each sample is the speed gain if the view
   * were rotated by that angle; map the angle to the screen with the same
   * projection as the game FOV (rectilinear at panini strength 0).
   */
  private drawCgaz(
    profile: Float32Array | number[] | null,
    width: number,
    project: (deg: number) => number,
    moving: boolean,
    style: CgazStyle,
  ): void {
    const ctx = this.cgazCtx;
    if (!ctx) return;
    const cw = Math.max(200, Math.round(width));
    const ch = Math.round(clamp(style.height, 2, 40));
    if (this.cgazCanvas.width !== cw || this.cgazCanvas.height !== ch) {
      this.cgazCanvas.width = cw;
      this.cgazCanvas.height = ch;
      this.cgazHidden = false;
    }
    if (this.lastCgazHeight !== ch) {
      this.lastCgazHeight = ch;
      this.cgazCanvas.style.height = `${ch}px`;
    }
    const hide = () => {
      if (this.cgazHidden) return;
      this.cgazHidden = true;
      ctx.clearRect(0, 0, cw, ch);
      this.lastCgazOpacity = "0";
      this.cgazCanvas.style.opacity = "0";
    };
    if (!profile || profile.length < 2 || !moving) {
      hide();
      return;
    }

    let maxG = -Infinity;
    let minG = Infinity;
    for (const g of profile) {
      if (g > maxG) maxG = g;
      if (g < minG) minG = g;
    }
    // All-zero / near-zero: nothing to show.
    if (Math.abs(maxG) < 1e-5 && Math.abs(minG) < 1e-5) {
      hide();
      return;
    }

    const posMax = Math.max(0, maxG);
    const eps = Math.max(1e-4, posMax * 0.02);
    const fastT = posMax * 0.98;
    const n = profile.length;
    const step = (2 * CGAZ_RANGE_DEG) / (n - 1);
    const cx = cw / 2;
    // A positive sampled angle is a view rotation to the left (CCW); on screen
    // it appears left of centre, so project(-angle).
    const xFor = (angle: number): number => cx + project(-angle);

    const zoneColor = (z: CgazZone): string => rgbaHex(style.colors[z], style.alphas[z]);
    const C_DEAD = zoneColor("dead");
    const C_PARTIAL = zoneColor("partial");
    const C_FAST = zoneColor("fast");
    const C_TURN = zoneColor("turn");
    const C_DECEL = zoneColor("decel");
    const colorAt = (angle: number, g: number): string => {
      if (g < -eps) return C_DECEL;
      if (g <= eps) return C_DEAD;
      if (Math.abs(angle) > 90) return C_TURN;
      if (posMax > 0 && g >= fastT) return C_FAST;
      return C_PARTIAL;
    };

    // Keep the strip readable: draw a central band, not the full screen.
    this.cgazHidden = false;
    const bandHalf = Math.min(cw / 2, clamp(style.width, 160, 1600) / 2);
    const bandW = bandHalf * 2;
    const top = 1;
    const bh = Math.max(1, ch - 2);
    ctx.clearRect(0, 0, cw, ch);
    ctx.save();
    ctx.beginPath();
    ctx.rect(cx - bandHalf, 0, bandW, ch);
    ctx.clip();
    if (style.bgOpacity > 0) {
      ctx.fillStyle = `rgba(0,0,0,${style.bgOpacity})`;
      ctx.fillRect(cx - bandHalf, top, bandW, bh);
    }

    const drawRun = (a0: number, a1: number, color: string) => {
      const x0 = xFor(a0);
      const x1 = xFor(a1);
      const left = Math.min(x0, x1);
      const runW = Math.abs(x1 - x0);
      if (runW < 0.5) return;
      ctx.fillStyle = color;
      ctx.fillRect(left, top, runW, bh);
    };

    let runStart = -CGAZ_RANGE_DEG;
    let runColor = colorAt(-CGAZ_RANGE_DEG + step / 2, (profile[0] + profile[1]) / 2);
    let prevAngle = -CGAZ_RANGE_DEG;
    for (let i = 1; i < n; i++) {
      const angle = -CGAZ_RANGE_DEG + i * step;
      const mid = (prevAngle + angle) / 2;
      const color = colorAt(mid, (profile[i - 1] + profile[i]) / 2);
      if (color !== runColor) {
        drawRun(runStart, prevAngle, runColor);
        runStart = prevAngle;
        runColor = color;
      }
      prevAngle = angle;
    }
    drawRun(runStart, CGAZ_RANGE_DEG, runColor);
    ctx.restore();

    // Optional outline around the band.
    if (style.border) {
      const bw = clamp(style.borderWidth, 1, 6);
      ctx.strokeStyle = rgbaHex(style.borderColor, style.borderOpacity);
      ctx.lineWidth = bw;
      ctx.strokeRect(
        cx - bandHalf + bw / 2,
        top + bw / 2,
        bandW - bw,
        Math.max(1, bh - bw),
      );
    }

    // Centre tick + faint 45°/90° guides.
    const guideH = Math.min(3, bh);
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fillRect(cx - 0.5, top, 1, bh);
    ctx.fillStyle = "rgba(255,255,255,0.25)";
    for (const a of [-90, -45, 45, 90]) {
      const x = Math.round(xFor(a));
      ctx.fillRect(x, top, 1, guideH);
    }
    const filter = style.shadow
      ? "drop-shadow(0 1px 2px rgba(0,0,0,0.9))"
      : "none";
    if (filter !== this.lastCgazFilter) {
      this.lastCgazFilter = filter;
      this.cgazCanvas.style.filter = filter;
    }
    const opacity = String(clamp(style.opacity, 0, 1));
    if (opacity !== this.lastCgazOpacity) {
      this.lastCgazOpacity = opacity;
      this.cgazCanvas.style.opacity = opacity;
    }
  }
}
